import { useLocale } from '@/contexts/LocaleContext';
import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { Outlet, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useHubTabs } from '@/lib/permisos';
import {
  Building2, Users, ShoppingBag, MapPin, Clock, UserCog, Workflow, LayoutGrid,
  KeyRound, Shield, Building, BedDouble, Wrench, Stethoscope, UtensilsCrossed, Flower2,
} from 'lucide-react';
import api from '@/lib/api';
import { tenantHasVertical } from '@/lib/verticalAccess';

export default function BusinessHubLayout() {
  const { t } = useLocale();
  const location = useLocation();
  const [dict, setDict] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDict = () => {
    api.get('/tenant/business/dictionary')
      .then(r => setDict(r.data))
      .catch(() => setDict({ vertical: 'generic', location_label: 'Sede', dictionary: {}, is_developer: false, secondary_verticals: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDict(); }, []);


  const locLabel = dict?.location_label || 'Sede';
  // Tropicalización: pestañas de operación solo por vertical (o secondary_verticals).
  // is_corp_account NO desbloquea hotel/taller/clínica/restaurante/spa/inmobiliaria.
  const isRealEstate = tenantHasVertical(dict, 'real_estate');
  const isHotel = tenantHasVertical(dict, 'hotel');
  const isTaller = tenantHasVertical(dict, 'workshop');
  const isClinica = tenantHasVertical(dict, 'clinic');
  const isRestaurante = tenantHasVertical(dict, 'restaurant');
  const isSpa = tenantHasVertical(dict, 'spa');
  // Canónico del API (business_profile o subtype «Desarrollador…»).
  const isDeveloper = !!(dict?.is_developer);

  const pluralize = (s) => /[aeiouáéíóú]$/i.test(s) ? s + 's' : s + 'es';

  const unitLabel = dict?.unit_label || dict?.dictionary?.unit || 'Unidad';
  const unitsTabLabel = isDeveloper ? pluralize(unitLabel) : 'Catálogo';
  const locationsLabel = isDeveloper
    ? 'Salas de venta'
    : (isRealEstate ? 'Oficinas' : pluralize(locLabel));

  // Orden para Desarrollador: Desarrollos primero en inventario, luego Unidades.
  const tabs = [
    { key: 'resumen', path: '/tenant/business/resumen', labelKey: 'biz.summary', icon: LayoutGrid, module: 'settings' },
    { key: 'team', path: '/tenant/business/team', labelKey: 'biz.team', icon: Users, module: 'users' },
    { key: 'assignment', path: '/tenant/business/assignment', labelKey: 'biz.assignment', icon: UserCog, module: 'assignment' },
  ];
  if (isRealEstate && isDeveloper) {
    tabs.push({ key: 'developments', path: '/tenant/business/developments', label: 'Desarrollos', icon: Building, module: 'real_estate_erp' });
    tabs.push({ key: 'catalog', path: '/tenant/business/catalog', label: unitsTabLabel, icon: ShoppingBag, module: 'inventory' });
  } else {
    tabs.push({ key: 'catalog', path: '/tenant/business/catalog', label: unitsTabLabel, icon: ShoppingBag, module: 'inventory' });
  }
  if (isHotel) {
    tabs.push({ key: 'hotel', path: '/tenant/business/hotel', label: 'Recepción', icon: BedDouble, module: 'hotel' });
  }
  if (isTaller) {
    tabs.push({ key: 'taller', path: '/tenant/business/taller', label: 'Órdenes de servicio', icon: Wrench, module: 'taller' });
  }
  if (isClinica) {
    tabs.push({ key: 'clinica', path: '/tenant/business/clinica', label: 'Consultorio', icon: Stethoscope, module: 'expediente' });
  }
  if (isRestaurante) {
    tabs.push({ key: 'restaurante', path: '/tenant/business/restaurante', label: 'Restaurante', icon: UtensilsCrossed, module: 'restaurante' });
  }
  if (isSpa) {
    tabs.push({ key: 'spa', path: '/tenant/business/spa', label: 'Spa', icon: Flower2, module: 'spa' });
  }
  tabs.push({ key: 'locations', path: '/tenant/business/locations', label: locationsLabel, icon: MapPin, module: 'locations' });
  tabs.push({ key: 'flows', path: '/tenant/business/flows', labelKey: 'biz.flows', icon: Workflow, module: 'flows' });
  tabs.push({ key: 'profile', path: '/tenant/business/profile', labelKey: 'biz.profile', icon: Clock, module: 'settings' });
  tabs.push({ key: 'connections', path: '/tenant/business/connections', label: 'Mis conexiones', icon: KeyRound, module: 'integrations' });
  tabs.push({ key: 'permissions', path: '/tenant/business/permissions', label: 'Permisos', icon: Shield, module: 'users' });

  const { tabs: visibleTabs, sinAcceso } = useHubTabs(tabs);

  if (loading) return <PantallaCargando />;

  if (location.pathname === '/tenant/business' || location.pathname === '/tenant/business/') {
    return <Navigate to={isRealEstate && isDeveloper ? '/tenant/business/developments' : '/tenant/business/resumen'} replace />;
  }

  const subtitle = isDeveloper
    ? 'Desarrollos, unidades por proyecto, equipo y salas de venta'
    : `Configura tu equipo, catálogo, horario y ${pluralize(locLabel).toLowerCase()}`;

  return (
    <div className="space-y-5" data-testid="business-hub" data-developer={isDeveloper ? '1' : '0'}>
      <div>
        <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight flex items-center gap-2">
          <Building2 className="w-6 h-6" /> {t('nav.business')}
        </h1>
        <p className="text-sm text-[#64748B] mt-1">{subtitle}</p>
      </div>
      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto" data-testid="business-hub-tabs">
        {visibleTabs.map(tab => (
          <NavLink key={tab.key} to={tab.path} data-testid={`business-tab-${tab.key}`}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                isActive ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}>
            <tab.icon className="w-4 h-4" /> {tab.labelKey ? t(tab.labelKey) : tab.label}
          </NavLink>
        ))}
      </div>
      <Outlet context={{ dict, refetchDict: fetchDict, isDeveloper }} />
    </div>
  );
}
